<template>
	<div>
		<AppDialog @onCancel="cancel" max-width="530">
			<div class="preview-settings-dialog text-align-center pa-6">
				<div class="dialog-title">Preview settings</div>
				<div class="dialog-description mb-3">
					Tip: preview samples anywhere by holding <span class="keyboard-key">Alt</span> Or <span class="keyboard-key">Option ⌥</span>
				</div>
				<div class="dialog-body">
					<AppSelect height="22" outlined dense v-model="selectedOption" :items="options" item-text="displayText" item-value="id" />
					<div class="preview-section mt-6">
						<div class="mb-3 font-size-xs">Preview:</div>
						<div
							v-for="data in previewData"
							:key="data.label"
							class="preview-row-layout mb-3"
							:class="{ 'is-plain-text': data.isPlainTextSource }"
						>
							<div>{{ data.label }}</div>
							<div class="preview-items">
								<div class="inner-box-layout" v-if="!isSample">
									<div><ImageIndicatorSVG /></div>
									<div><component :is="data.dataTypeSVG" /></div>
									<div class="source-indicator-text">{{ data.source }}</div>
								</div>
								<div class="sample-indicator-text" :class="{ 'is-sample': isSample }" v-if="!isSource">{{ data.sample }}</div>
							</div>
						</div>
					</div>
				</div>

				<div class="dialog-bottom text-align-center mt-4">
					<SecondaryButton @click="close">Done</SecondaryButton>
				</div>
			</div>
		</AppDialog>
	</div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';

// import { editNode, insertedDataDisplayMode, insertedDataDisplayModeMap } from '@/store/modules/selectedNode/getters';
import { editNode } from '@/store/modules/selectedNode/getters';
import { insertedDataDisplayMode, insertedDataDisplayModeMap } from '@/store/modules/richText/getters';
import { setInsertedDataDisplayMode } from '@/store/modules/richText/actions';

import AppDialog from '@/components/dialogs/AppDialog/AppDialog';

import AppDialogManagerService from '@/components/dialogs/AppDialogManager/app-dialog-manager-service';

import ImageIndicatorSVG from './assets/ImageIndicatorSVG';
import TableIndicatorSVG from './assets/TableIndicatorSVG.vue';
import TableTextIndicatorSVG from './assets/TableTextIndicatorSVG.vue';
import TextIndicatorSVG from './assets/TextIndicatorSVG.vue';

export default {
	name: 'PreviewSettingsDialog',
	components: {
		AppDialog,
		ImageIndicatorSVG,
	},
	props: {
		dialogPromiseHandlers: { type: Object },
	},
	computed: {
		...mapGetters([editNode.fullPath, insertedDataDisplayMode.fullPath, insertedDataDisplayModeMap.fullPath]),
		options() {
			return Object.values(this[insertedDataDisplayModeMap.fullPath]);
		},
		selectedOption: {
			get() {
				return this[insertedDataDisplayMode.fullPath];
			},
			set(newV) {
				this[setInsertedDataDisplayMode.fullPath](newV);
			},
		},
		isSource() {
			return this['richText/insertedDataDisplayMode'] === this['richText/insertedDataDisplayModeMap'].SOURCE.id;
		},
		isSample() {
			return this['richText/insertedDataDisplayMode'] === this['richText/insertedDataDisplayModeMap'].SAMPLE.id;
		},
	},
	methods: {
		...mapActions([setInsertedDataDisplayMode.fullPath]),
		cancel() {
			this.dialogPromiseHandlers.reject();
		},
		close() {
			AppDialogManagerService.closeCurrentDialog(this.optionsLocal);
		},
	},
	data() {
		return {
			previewData: [
				{
					label: 'Item',
					dataTypeSVG: TextIndicatorSVG,
					dataTypeIconName: 'text-indicator',
					source: 'Source',
					sample: 'sample',
					isPlainTextSource: true,
				},
				{
					label: 'Column',
					dataTypeSVG: TableTextIndicatorSVG,
					dataTypeIconName: 'table-text-indicator',
					source: 'Table name | property name',
					sample: '(1) sample',
				},
				{ label: 'Table', dataTypeSVG: TableIndicatorSVG, dataTypeIconName: 'table-indicator', source: 'Table name', sample: '23 items' },
			],
		};
	},
};
</script>

<style scoped lang="scss">
.preview-settings-dialog {
	position: relative;
	text-align: left;

	.preview-section {
		margin: 4px 0;

		.preview-row-layout {
			display: flex;
			align-items: center;

			> :nth-child(1) {
				flex: 1;
			}

			> :nth-child(2) {
				flex: 2;
			}

			.preview-items {
				display: inline-flex;

				.inner-box-layout {
					display: inline-flex;
					border: 1px solid var(--border-color);
					border-radius: 4px;

					> * {
						min-width: 26px;
						padding: 4px;
						display: flex;
						align-items: center;
						justify-content: center;

						&:not(:last-child):not(.source-indicator-text) {
							border-right: 1px solid var(--border-color);
						}
					}
				}

				.source-indicator-text {
					position: relative;
					background-color: var(--border-color);
				}

				.sample-indicator-text {
					display: flex;
					align-items: center;
					padding: 4px;
					border-radius: 4px;
					background-color: var(--soft-grey-background);
					z-index: -1;

					&:not(.is-sample) {
						transform: translateX(-6px);
						padding-left: 10px;
					}
				}
			}

			&:not(.is-plain-text) {
				.source-indicator-text {
					background-color: var(--table-column-background);
				}
				.sample-indicator-text {
					background-color: var(--table-column-background-blured);
				}
			}
		}
	}

	.dialog-bottom {
		::v-deep {
			button {
				padding-left: 40px;
				padding-right: 40px;
			}
		}
	}
}
</style>
